import { WindLeavesConfig } from "game-common/models"

// The room's wind, shared by everything that answers to it. There is ONE wind in a room; the leaves
// and the bending grass are two ways of seeing it, and the fog drifts on it too. If each effect rolled
// its own gust envelope they would surge at different moments, which looks worse than no wind at all
// -- the eye reads a field of grass leaning one way while the leaves above it blow another as a bug
// long before it can say what the bug is.
//
// So the envelope lives here and the room's WindLeavesConfig is read as "the room's wind" rather than
// as the leaves' private settings.

export interface Wind {
    // Unit heading. 0 degrees is due east (+x), 90 due south (+y).
    x: number
    y: number
    // The shared gust multiplier at this instant, around 1, never at or below zero.
    gust: number
}

export const DEFAULT_DIRECTION_DEG = 22
export const DEFAULT_GUSTINESS = 0.6

/**
 * The gust envelope: two slow, incommensurate sines summed, so the surges never settle into a rhythm
 * the eye can predict. Floored above zero -- a gust that reaches 0 leaves a field hanging motionless
 * in mid-air, which looks broken rather than calm.
 */
export const gustAt = (tSeconds: number, gustiness: number): number =>
    Math.max(0.15, 1 + gustiness * (0.62 * Math.sin(tSeconds * 0.41) + 0.38 * Math.sin(tSeconds * 1.13 + 1.7)))

/**
 * The room's wind at this instant, or null if the room has none.
 *
 * Console overrides are honoured here so that turning the wind up while looking at it moves the leaves
 * and the grass together.
 */
export const windAt = (config: WindLeavesConfig | null, tSeconds: number): Wind | null => {
    const w = window as any
    const forced = typeof w.windLeaves === "boolean" ? (w.windLeaves as boolean) : undefined
    const enabled = forced !== undefined ? forced : !!config?.enabled
    if (!enabled) {
        return null
    }

    const num = (override: any, value: number | undefined, fallback: number) =>
        typeof override === "number" ? override : typeof value === "number" ? value : fallback

    const rad = (num(w.windLeavesDirection, config?.directionDeg, DEFAULT_DIRECTION_DEG) * Math.PI) / 180
    const gustiness = Math.min(1, Math.max(0, num(w.windLeavesGust, config?.gustiness, DEFAULT_GUSTINESS)))

    return { x: Math.cos(rad), y: Math.sin(rad), gust: gustAt(tSeconds, gustiness) }
}
